import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import { StatusChip } from 'src/components/payments-ops/StatusChip'
import { ConfirmActionModal } from 'src/components/shared/ConfirmActionModal'
import { BasicSwitch } from 'src/components/shared/form/BasicSwitch'
import { SectionCard } from 'src/components/shared/SectionCard'
import { AdminToggleKillswitch } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import type { KillswitchFeature } from 'src/constants/payments-ops/enums'
import type { ChipTone } from 'src/constants/payments-ops/types'
import { KILLSWITCH_COPY } from 'src/constants/payments-ops/killswitchCopy'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { KillswitchState } from 'src/interfaces/usBankKillswitch'
import { getKillswitchFlagsLoading, getKillswitchToggling } from 'src/selectors/usBankKillswitch'
import { useKillswitchStatusKnown } from 'src/utils/hooks/paymentsOps/useKillswitchStatusKnown'
import { usePartnerNameMap } from 'src/utils/hooks/paymentsOps/usePartnerNameMap'
import { resolvePartnerName } from 'src/utils/paymentsOps/partnerNames'

interface Props {
  feature: KillswitchFeature
  state: KillswitchState
}

const KillswitchCardComponent = ({ feature, state }: Props) => {
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const nameMap = usePartnerNameMap()
  const isToggling = useSelector(getKillswitchToggling)
  const isLoading = useSelector(getKillswitchFlagsLoading)
  const isStatusKnown = useKillswitchStatusKnown()
  // The switch PATCHes / POSTs `/feature-flags` (`feature_flags_manage`). Without it the card still
  // shows the state from the flags GET, read-only (spec §7.5).
  const canToggle = useHasPermission(InternalPermission.FeatureFlagsManage)

  // The enabled value the admin asked for, awaiting confirmation. `null` = no modal.
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null)

  const copy = KILLSWITCH_COPY[feature]
  const isTurningOn = pendingEnabled === true

  const partnerNames = useMemo(() => {
    return state.activePartnerIds
      .map((partnerId) => {
        return resolvePartnerName(nameMap, partnerId)
      })
      .join(', ')
  }, [nameMap, state.activePartnerIds])

  const chip = useMemo<{ label: string; tone: ChipTone }>(() => {
    if (!isStatusKnown) {
      return { label: isLoading ? 'Checking status…' : 'Status unavailable', tone: 'neutral' }
    }

    return state.isGloballyActive
      ? { label: copy.activeChip, tone: 'critical' }
      : { label: copy.inactiveChip, tone: 'success' }
  }, [copy.activeChip, copy.inactiveChip, isLoading, isStatusKnown, state.isGloballyActive])

  const handleSwitch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Never flip immediately — every change goes through a confirmation stating its scope.
      // With the status unknown there is no row to patch and a toggle would POST a create.
      if (!isStatusKnown) {
        return
      }

      setPendingEnabled(event.target.checked)
    },
    [isStatusKnown],
  )

  const handleClose = useCallback(() => {
    // The write is in flight: closing now would hide its outcome behind an unchanged switch.
    if (isToggling) {
      return
    }

    setPendingEnabled(null)
  }, [isToggling])

  const handleConfirm = useCallback(async () => {
    if (pendingEnabled === null || !isStatusKnown) {
      return
    }

    const nextEnabled = pendingEnabled
    const result = await asyncDispatchWithErrorToast(
      AdminToggleKillswitch.request({
        feature,
        enabled: nextEnabled,
        flagId: state.globalFlag?.id ?? null,
      }),
    )

    setPendingEnabled(null)

    if (result) {
      snackbar?.enqueueSnackbar(nextEnabled ? copy.turnedOnToast : copy.turnedOffToast, {
        variant: nextEnabled ? 'warning' : 'success',
      })
    }
  }, [
    asyncDispatchWithErrorToast,
    copy.turnedOffToast,
    copy.turnedOnToast,
    feature,
    isStatusKnown,
    pendingEnabled,
    snackbar,
    state.globalFlag?.id,
  ])

  return (
    <Fragment>
      <SectionCard
        title={copy.title}
        dataTestId={`killswitch-card-${feature}`}
        actions={<StatusChip label={chip.label} tone={chip.tone} />}
      >
        <div css={tw`flex flex-row items-start justify-between gap-6`}>
          <div css={tw`flex flex-col gap-2`}>
            <Typography variant="body2">{copy.scope}</Typography>
            {state.activePartnerIds.length > 0 ? (
              <Typography variant="body2" css={tw`text-xs text-warning-700`}>
                Also active for {state.activePartnerIds.length} partner
                {state.activePartnerIds.length === 1 ? '' : 's'} on their own: {partnerNames}.
              </Typography>
            ) : null}
          </div>
          {canToggle ? (
            <BasicSwitch
              name={`killswitch-${feature}`}
              ariaLabel={copy.title}
              checked={state.isGloballyActive}
              disabled={isToggling || isLoading || !isStatusKnown}
              onChange={handleSwitch}
            />
          ) : null}
        </div>
      </SectionCard>
      <ConfirmActionModal
        isOpen={pendingEnabled !== null}
        title={isTurningOn ? copy.turnOnTitle : copy.turnOffTitle}
        description={isTurningOn ? copy.turnOnDescription : copy.turnOffDescription}
        acknowledgement={
          isTurningOn ? 'I understand this affects every partner on the platform.' : undefined
        }
        confirmLabel={isTurningOn ? copy.turnOnConfirm : copy.turnOffConfirm}
        isDestructive={isTurningOn}
        isSubmitting={isToggling}
        onConfirm={handleConfirm}
        onClose={handleClose}
        confirmDataTestId={`killswitch-confirm-${feature}`}
      />
    </Fragment>
  )
}

export const KillswitchCard = memo(KillswitchCardComponent)
